"use client";

import React, { createContext, useContext, useState, useEffect } from 'react';
import { trackClientEvent } from '@/lib/analyticsClient';
import { toast } from 'sonner';
import { usePathname } from 'next/navigation';
import { useRouter } from 'next/navigation';
import { useSession } from 'next-auth/react';
import { OnboardingInterestsModal } from './onboarding-interests-modal';
import { TourStepModal, type TourStepInfo } from './tour-step-modal';
import { WheelOnboardingProgress } from './wheel-onboarding-progress';
import { BrokerConnectStep } from './broker-connect-step';
import { OnboardingPricingModal } from './onboarding-pricing-modal';
import { ExpiredSubscriptionGate } from './expired-subscription-gate';
import { useFeatureFlag } from '@/hooks/use-feature-flags';
import { getSandboxInterests, setSandboxInterests, clearSandboxOnboarding } from '@/lib/sandbox-onboarding';
import { OnboardingModeContext, WHEEL_STAGES, stageIndex, stageHref, sanitizeWheelStage, type WheelOnboardingStage, GEX_STAGES, GEX_STAGES_WITH_HUB, gexStageHref, type GexOnboardingStage, type OnboardingModeContextType } from '@/hooks/use-onboarding-mode';
import { GexOnboardingProgress } from './gex-onboarding-progress';
import { WheelTourExitGuard, useWheelTourPathGuard } from './wheel-tour-exit-guard';
import { createWheelTourProgressQueue, isSameAppPath, issueWheelTourCreditProof, markWheelTourCreditEligible, requestWheelTourCredit } from '@/lib/wheelTourConversion';
import { useConnectionUp } from '@/hooks/use-connection-status';
import { subscribeConnection, getConnectionSnapshot, reportServerReachable, reportServerUnreachable } from '@/lib/connection-status';
import type { WheelTourProgressStage } from '@/lib/wheelTourProgress';

// At the top of the file, add this interface declaration
declare global {
  interface Window {
    __shepherdFilterButton?: Element;
    __shepherdAlertButton?: Element;
    __tourMobileButtonClicked?: boolean;
    __sidebarExpansionInProgress?: boolean;
  }
}

type TourContextType = {
  startTour: () => void;
  startTourAtStep: (stepId: string) => void;
  startModule: (moduleId: string) => void;
  endTour: () => void;
  isActive: boolean;
  activeModuleId: string | null;
  updateOnboardingStatus: (completed: boolean) => Promise<void>;
  startWheelOnboarding: (flow: 'wheel' | 'gex-first', quickRecap?: boolean) => void;
  startGexOnboarding: () => void;
};

const TourContext = createContext<TourContextType | null>(null);

export const useTour = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};

// Onboarding Context for sharing onboarding completion state
type OnboardingContextType = {
  hasCompletedOnboarding: boolean;
  setHasCompletedOnboarding: (completed: boolean) => void;
  onboardingInterests: string[];
};

const OnboardingContext = createContext<OnboardingContextType | null>(null);

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within a TourProvider');
  }
  return context;
};

type TourProviderProps = {
  children: React.ReactNode;
  /**
   * When false, every tour-related modal/intro is suppressed so the
   * dashboard-level TermsAcceptanceGate is the only thing the user can
   * interact with. Defaults to true so sandbox / non-authenticated
   * surfaces (which never render the terms gate) are unaffected.
   */
  termsAccepted?: boolean;
};

function getFirstStepIdForInterest(interests: string[]): string {
  if (interests.includes('directional_trader') && !interests.includes('premium_collector')) {
    return 'explore-gex';
  }
  if (interests.includes('premium_collector')) return 'create-watchlist';
  // legacy fallbacks
  const first = interests?.[0];
  if (first === 'screen') return 'options-table';
  if (first === 'journal') return 'connect-brokers';
  if (first === 'gex') return 'explore-gex';
  if (first === 'rolls') return 'avoid-assignment';
  return 'create-watchlist';
}

// Map legacy step ids to module ids (for startTour / startTourAtStep)
const STEP_ID_TO_MODULE_ID: Record<string, string> = {
  'create-watchlist': 'create-watchlist',
  'connect-brokers': 'connect-broker',
  'options-table': 'explore-screener',
  'watchlists': 'create-watchlist',
  'filters': 'save-search',
  'save-search': 'save-search',
  'avoid-assignment': 'avoid-assignment',
  'journal': 'connect-broker',
  'explore-gex': 'explore-gex',
  'tour-complete': 'explore-screener',
};

// Modular onboarding: 7 modules matching achievements, each with sub-steps
export type TourSubStepCompletionEvent = 'click' | 'change' | 'navigate' | 'manual' | 'custom';

export interface TourSubStep {
  id: string;
  title: string;
  text: string;
  highlightTarget: string;
  /** Fallback if highlightTarget not in DOM (e.g. desktop vs mobile) */
  highlightFallbackTarget?: string;
  completionEvent: TourSubStepCompletionEvent;
  completionSelector?: string;
  completionCheck?: () => boolean;
  /** When completionEvent is 'custom', page dispatches tour-substep-complete with this subStepId */
  completionCustomEvent?: string;
}

export interface TourModuleDef {
  id: string;
  title: string;
  achievementKey: string;
  targetPath: string;
  subSteps: TourSubStep[];
}

export const TOUR_MODULES: TourModuleDef[] = [
  {
    id: 'explore-screener',
    title: 'Explore 1 ticker in screener',
    achievementKey: 'achievedExploreScreen',
    targetPath: '/dashboard/options',
    subSteps: [
      { id: 's1-ticker', title: 'Enter a ticker', text: 'Type a ticker (e.g. AAPL, TSLA) in the quick search and submit to load options.', highlightTarget: 'ticker-quick-search', completionEvent: 'change', completionSelector: '[data-shepherd-highlight-target="ticker-quick-search"] input' },
      { id: 's2-filters', title: 'Open filters', text: 'Click the <b>Filters</b> button to set strategy and risk (CSP, Delta range).', highlightTarget: 'options-filters', highlightFallbackTarget: 'options-filters-mobile', completionEvent: 'click', completionSelector: '[data-shepherd-highlight-target="options-filters"], [data-shepherd-highlight-target="options-filters-mobile"]' },
      { id: 's3-expand', title: 'Expand one row & explore details', text: 'Click a row to expand it and see full details.', highlightTarget: 'row-expand-first', completionEvent: 'click', completionSelector: '[data-shepherd-highlight-target="row-expand-first"]' },
      { id: 's4-analyze', title: 'Click Analyze with AI', text: 'In the expanded row, click <b>Analyze Risk with AI</b> to get an AI summary.', highlightTarget: 'analyze-risk-ai', completionEvent: 'click', completionSelector: '[data-shepherd-highlight-target="analyze-risk-ai"]' },
      { id: 's5-trade', title: 'Click Trade', text: 'Click <b>Copy Trade</b> to copy the trade to your broker. (You can connect a broker in another module.)', highlightTarget: 'copy-trade-button', completionEvent: 'click', completionSelector: '[data-shepherd-highlight-target="copy-trade-button"]' },
      { id: 's6-filter', title: 'Modify 1 filter', text: 'Open the filters panel and change at least one filter (e.g. Yield or Delta).', highlightTarget: 'options-filters', completionEvent: 'click', completionSelector: '[data-shepherd-highlight-target="options-filters"]' },
      { id: 's7-save', title: 'Save the search', text: 'Click <b>Save</b> to store your search and optionally set up alerts.', highlightTarget: 'save-search', completionEvent: 'click', completionSelector: '[data-shepherd-highlight-target="save-search"]' },
    ],
  },
  {
    id: 'create-watchlist',
    title: 'Create a watchlist',
    achievementKey: 'achievedAddWatchlist',
    targetPath: '/dashboard/watchlists',
    subSteps: [
      { id: 'w1-create', title: 'Click "New" to create a watchlist', text: 'Click the <b>New</b> button to create your own watchlist. Watchlists let you focus on stocks you are comfortable owning.', highlightTarget: 'create-watchlist-btn', completionEvent: 'click', completionSelector: '[data-shepherd-highlight-target="create-watchlist-btn"]' },
      { id: 'w2-name', title: 'Name your watchlist', text: 'Type a name for your watchlist and hit <b>Create</b>.', highlightTarget: 'create-watchlist-btn', completionEvent: 'manual' },
      { id: 'w3-add-ticker', title: 'Add a ticker', text: 'Type a ticker (e.g. AAPL) in the search box and click <b>+</b> to add it.', highlightTarget: 'add-stock-to-watchlist', completionEvent: 'manual' },
      { id: 'w4-done', title: 'Done!', text: 'You created a watchlist and added a stock. Use it to filter opportunities in the screener.', highlightTarget: 'add-stock-to-watchlist', completionEvent: 'manual' },
    ],
  },
  {
    id: 'connect-broker',
    title: 'Connect a broker',
    achievementKey: 'achievedConnectBroker',
    targetPath: '/dashboard/broker-integration',
    subSteps: [
      { id: 'b1-manual', title: 'Manual Journal', text: 'Use <b>Manual Journal</b> to log trades by hand - perfect for paper trading or any broker.', highlightTarget: 'manual-journal-card', completionEvent: 'manual' },
      { id: 'b2-csv', title: 'CSV Import', text: 'Use <b>CSV Import</b> to upload trade history from any broker via CSV files.', highlightTarget: 'csv-import-card', completionEvent: 'manual' },
      { id: 'b3-oauth', title: 'Connect your broker', text: 'Pick your real broker (e.g. E*Trade, Schwab, Webull) and click <b>Connect</b>. OAuth gives secure, read-only access. New accounts may take ~10 min to process and show in Journal.', highlightTarget: 'broker-integration-page', completionEvent: 'manual' },
      { id: 'b4-multiple', title: 'Multiple accounts', text: 'You can connect <b>multiple broker accounts</b>. Each shows as a separate connection. Connecting a broker unlocks <b>Copy Trade</b> - one-click trade execution.', highlightTarget: 'broker-integration-page', completionEvent: 'manual' },
    ],
  },
  {
    id: 'save-search',
    title: 'Save a screener search',
    achievementKey: 'achievedSaveSearch',
    targetPath: '/dashboard/options',
    subSteps: [
      { id: 'f1-advanced', title: 'Switch to Advanced mode', text: 'Click the <b>Advanced</b> toggle to unlock the full filter panel.', highlightTarget: 'view-mode-toggle', completionEvent: 'click', completionSelector: '[data-shepherd-highlight-target="view-mode-toggle"] button:last-of-type' },
      { id: 'f2-filters', title: 'Open filters', text: 'Click the <b>Filters</b> button to expand the filter panel.', highlightTarget: 'options-filters', highlightFallbackTarget: 'options-filters-mobile', completionEvent: 'click', completionSelector: '[data-shepherd-highlight-target="options-filters"], [data-shepherd-highlight-target="options-filters-mobile"]' },
      { id: 'f3-adjust', title: 'Adjust a filter', text: 'Change at least one filter (e.g. Yield, Delta, Strike). The table updates in real time.', highlightTarget: 'options-filters', completionEvent: 'manual' },
      { id: 'f4-save-btn', title: 'Click Save', text: 'Click <b>Save</b> to store your search. You can set up email and Discord alerts.', highlightTarget: 'save-search', highlightFallbackTarget: 'save-search-mobile', completionEvent: 'click', completionSelector: '[data-shepherd-highlight-target="save-search"], [data-shepherd-highlight-target="save-search-mobile"]' },
      { id: 'f5-name', title: 'Name and confirm', text: 'Give your search a name and confirm. You can enable alerts for new opportunities.', highlightTarget: 'save-search', completionEvent: 'manual' },
    ],
  },
  {
    id: 'avoid-assignment',
    title: 'Find optimal rolls',
    achievementKey: 'achievedFindRolls',
    targetPath: '/dashboard/optimal-roll',
    subSteps: [
      { id: 'r1-ticker', title: 'Enter a ticker', text: 'Enter a ticker to find rolling opportunities for your short options.', highlightTarget: 'optimal-roll-ticker', completionEvent: 'change', completionSelector: '[data-shepherd-highlight-target="optimal-roll-ticker"]' },
      { id: 'r2-options', title: 'Select option details', text: 'Select expiration and strike if needed.', highlightTarget: 'optimal-roll-ticker', completionEvent: 'manual' },
      { id: 'r3-view', title: 'View rolling opportunities', text: 'Review the table of possible rolls.', highlightTarget: 'optimal-roll-ticker', completionEvent: 'manual' },
      { id: 'r4-explore', title: 'Explore a roll suggestion', text: 'Click into a suggestion to see details. This helps avoid assignment.', highlightTarget: 'optimal-roll-ticker', completionEvent: 'manual' },
    ],
  },
  {
    id: 'explore-gex',
    title: 'Explore GEX',
    achievementKey: 'achievedExploreGex',
    targetPath: '/dashboard/gex/gex-ai',
    subSteps: [
      { id: 'g1-ticker', title: 'Enter or select a ticker', text: 'Type a ticker and search, or click a suggested ticker to load expirations.', highlightTarget: 'gex-ai-ticker', completionEvent: 'custom', completionCustomEvent: 'g1-ticker' },
      { id: 'g2-expirations', title: 'Select expirations', text: 'Pick one or more expirations (up to 4). Often auto-selected.', highlightTarget: 'gex-ai-ticker', completionEvent: 'custom', completionCustomEvent: 'g2-expirations' },
      { id: 'g3-generate', title: 'Click Generate', text: 'Click <b>Generate</b> to compute GEX charts. This counts as completing ticker + expirations too.', highlightTarget: 'gex-ai-generate', completionEvent: 'click', completionSelector: '[data-shepherd-highlight-target="gex-ai-generate"]' },
      { id: 'g4-review', title: 'Review AI summary', text: 'Review the GEX charts and AI-generated summary.', highlightTarget: 'gex-ai-ticker', completionEvent: 'manual' },
    ],
  },
  {
    id: 'monetize-trading',
    title: 'Monetize your trading',
    achievementKey: 'achievedMonetize',
    targetPath: '/dashboard/copy-trading',
    subSteps: [
      { id: 'm1-cta', title: 'Become a trader', text: 'Click to set up your trader profile and let others copy your trades.', highlightTarget: 'copy-trading-cta', completionEvent: 'click', completionSelector: '[data-shepherd-highlight-target="copy-trading-cta"] a' },
      { id: 'm2-profile', title: 'Configure your trader profile', text: 'Fill in your display name, bio, and strategies. This creates your public trader page.', highlightTarget: 'trader-config-form', completionEvent: 'manual' },
      { id: 'm3-fee', title: 'Set your subscription fee', text: 'Scroll down to the Stripe section. Connect Stripe to charge subscribers. You keep 80%.', highlightTarget: 'trader-config-form', completionEvent: 'manual' },
      { id: 'm4-theme', title: 'Choose a theme', text: 'Pick a theme for your public landing page.', highlightTarget: 'trader-config-form', completionEvent: 'manual' },
      { id: 'm5-save', title: 'Save your config', text: 'Click Save Configuration to publish your trader profile.', highlightTarget: 'trader-config-save', completionEvent: 'click', completionSelector: '[data-shepherd-highlight-target="trader-config-save"]' },
      { id: 'm6-affiliate', title: 'Visit the Affiliate page', text: 'Click Affiliate in the sidebar to get your referral link. Earn commission when friends sign up.', highlightTarget: 'Affiliate-page', completionEvent: 'click', completionSelector: '[data-shepherd-highlight-target="Affiliate-page"]' },
    ],
  },
];

/**
 * next-auth reports `unauthenticated` both when the user is genuinely logged
 * out AND when its session fetch fails (server stopped/unreachable, dropped
 * connection) or its client state lags behind a reconnect. Confirm against
 * the session endpoint before downgrading anyone to free/sandbox defaults -
 * misreading a connection loss as "anonymous visitor" used to pop the
 * onboarding interests modal and lock navigation for paid users.
 */
async function confirmLoggedOut(): Promise<boolean> {
  try {
    const res = await fetch('/api/auth/session', { cache: 'no-store' });
    reportServerReachable();
    if (!res.ok) return false;
    const data = await res.json().catch(() => null);
    return !data?.user;
  } catch {
    reportServerUnreachable();
    return false;
  }
}

export const TOUR_SUBSTEP_COMPLETE_EVENT = 'tour-substep-complete';

export function dispatchTourSubstepComplete(moduleId: string, subStepId: string) {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(TOUR_SUBSTEP_COMPLETE_EVENT, { detail: { moduleId, subStepId } }));
  }
}

export const TourProvider: React.FC<TourProviderProps> = ({ children, termsAccepted = true }) => {
  const copyTradeEnabled = useFeatureFlag("tradingButton");
  // Missing/off is the new trial-first flow; on preserves the legacy broker-first handoff.
  const wheelTourBrokerFirst = useFeatureFlag("wheelTourBrokerFirst");

  // GEX Hub tour step is behind the "gexHub" flag. When off, the GEX tour
  // reverts to its original stage list (no hub).
  const gexHubEnabled = useFeatureFlag("gexHub");
  const gexStages = React.useMemo(
    () => (gexHubEnabled ? GEX_STAGES_WITH_HUB : GEX_STAGES),
    [gexHubEnabled]
  );
  // The tour always starts on GEX AI; the Hub (when enabled) is step 2.
  const firstGexStage: GexOnboardingStage = "gex-ai";

  // Compute effective tour modules: filter out copy-trade step when flag is off
  const effectiveTourModules = React.useMemo(() => {
    if (copyTradeEnabled) return TOUR_MODULES;
    return TOUR_MODULES.map((mod) => {
      if (mod.id !== 'explore-screener') return mod;
      return {
        ...mod,
        subSteps: mod.subSteps.filter((s) => s.id !== 's5-trade'),
      };
    });
  }, [copyTradeEnabled]);

  const [isActive, setIsActive] = useState(false);
  const [hasCheckedOnboarding, setHasCheckedOnboarding] = useState(false);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(true);
  const [onboardingInterests, setOnboardingInterests] = useState<string[]>([]);
  const [showPreferencesStep, setShowPreferencesStep] = useState(false);
  const [currentStepInfo, setCurrentStepInfo] = useState<TourStepInfo | null>(null);
  const [activeModuleId, setActiveModuleId] = useState<string | null>(null);
  const [moduleSubStepIndex, setModuleSubStepIndex] = useState(0);
  const [moduleCompletedSubSteps, setModuleCompletedSubSteps] = useState<string[]>([]);
  const [showNextModulePrompt, setShowNextModulePrompt] = useState<TourModuleDef | null>(null);
  const [completedModuleIds, setCompletedModuleIds] = useState<string[]>([]);
  const moduleListenerCleanupRef = React.useRef<(() => void) | null>(null);
  const moduleStartingRef = React.useRef(false);
  const pathname = usePathname();
  const router = useRouter();
  const { data: session, status: sessionStatus } = useSession();
  const onboardingScope = session?.user?.id || 'anon';

  // --- Wheel Onboarding Mode State ---
  const [wheelOnboardingActive, setWheelOnboardingActive] = useState(false);
  const [wheelFlow, setWheelFlow] = useState<'wheel' | 'gex-first' | null>(null);
  const [wheelStage, setWheelStage] = useState<WheelOnboardingStage>('find-deals');
  const [wheelCompletedStages, setWheelCompletedStages] = useState<WheelOnboardingStage[]>([]);
  const [wheelQuickRecap, setWheelQuickRecap] = useState(false);
  const [showBrokerConnect, setShowBrokerConnect] = useState(false);
  const [showOnboardingPricing, setShowOnboardingPricing] = useState(false);
  const [showExpiredGate, setShowExpiredGate] = useState(false);
  const [showWheelIntro, setShowWheelIntro] = useState(false);
  const [isFreeUser, setIsFreeUser] = useState(true);
  const [hadPreviousSubscription, setHadPreviousSubscription] = useState(false);
  const [isGexOnly, setIsGexOnly] = useState(false);
  const [isSpecialUser, setIsSpecialUser] = useState(false);
  const [subscriptionLoaded, setSubscriptionLoaded] = useState(false);

  // --- GEX Onboarding Mode State ---
  const [gexOnboardingActive, setGexOnboardingActive] = useState(false);
  const [gexStage, setGexStage] = useState<GexOnboardingStage>('gex-ai');
  const [gexCompletedStages, setGexCompletedStages] = useState<GexOnboardingStage[]>([]);
  const [showGexIntro, setShowGexIntro] = useState(false);

  // --- Tour guard modal (reward reminder during onboarding) ---
  const [showTourGuard, setShowTourGuard] = useState(false);

  // --- Tour completed flags (keep progress panels visible with Upgrade CTA) ---
  const [gexTourCompleted, setGexTourCompleted] = useState(false);
  const [wheelTourCompleted, setWheelTourCompleted] = useState(false);

  // --- Connectivity awareness ---
  // A lost connection (browser offline OR app server unreachable, e.g. the
  // server was stopped/redeployed) must never be misread as "new free user":
  // we keep last-known subscription/onboarding state while down and refetch
  // once the connection recovers (reconnectNonce re-runs the fetch effects).
  const connectionUp = useConnectionUp();
  const [reconnectNonce, setReconnectNonce] = useState(0);
  useEffect(() => {
    let wasUp = getConnectionSnapshot();
    return subscribeConnection(() => {
      const up = getConnectionSnapshot();
      if (!wasUp && up) setReconnectNonce((n) => n + 1);
      wasUp = up;
    });
  }, []);

  // --- Broker connection detection (show real data for connected free users) ---
  const [hasBrokerConnected, setHasBrokerConnected] = useState(false);
  const restoredRef = React.useRef(false);
  const subscriptionRefetchedForStripeReturn = React.useRef(false);
  const wheelProgressRef = React.useRef(createWheelTourProgressQueue());

  useWheelTourPathGuard({
    enabled: subscriptionLoaded && wheelOnboardingActive && isFreeUser && !isSpecialUser
      && !showWheelIntro && !showBrokerConnect && !showOnboardingPricing && !showTourGuard,
    pathname: pathname || '',
    expectedPath: stageHref(wheelStage),
    onDrift: () => setShowTourGuard(true),
    onReturn: (path) => router.replace(path),
  });

  // --- Onboarding state persistence helpers ---
  const WHEEL_STORAGE_KEY_LEGACY = 'qw_wheel_onboarding_state';
  const GEX_STORAGE_KEY_LEGACY = 'qw_gex_onboarding_state';
  const wheelStorageKey = `qw_wheel_onboarding_state:${onboardingScope}`;
  const gexStorageKey = `qw_gex_onboarding_state:${onboardingScope}`;
  const lastOnboardingUserKey = 'qw_onboarding_last_user_id';

  const getScopedState = React.useCallback((key: string, legacyKey: string, allowLegacy: boolean) => {
    try {
      const scoped = localStorage.getItem(key);
      if (scoped) return scoped;
      if (!allowLegacy) return null;
      const legacy = localStorage.getItem(legacyKey);
      if (legacy) {
        localStorage.setItem(key, legacy);
      }
      return legacy;
    } catch {
      return null;
    }
  }, []);

  const saveWheelState = React.useCallback((stage: WheelOnboardingStage, completed: WheelOnboardingStage[], active: boolean) => {
    try { localStorage.setItem(wheelStorageKey, JSON.stringify({ stage, completedStages: completed, active })); } catch {}
  }, [wheelStorageKey]);

  const saveGexState = React.useCallback((stage: GexOnboardingStage, completed: GexOnboardingStage[], active: boolean) => {
    try { localStorage.setItem(gexStorageKey, JSON.stringify({ stage, completedStages: completed, active })); } catch {}
  }, [gexStorageKey]);

  const clearWheelState = React.useCallback(() => {
    try {
      localStorage.removeItem(wheelStorageKey);
      localStorage.removeItem(WHEEL_STORAGE_KEY_LEGACY);
    } catch {}
  }, [wheelStorageKey]);

  const clearGexState = React.useCallback(() => {
    try {
      localStorage.removeItem(gexStorageKey);
      localStorage.removeItem(GEX_STORAGE_KEY_LEGACY);
    } catch {}
  }, [gexStorageKey]);

  const clearAllTourState = React.useCallback(() => {
    try {
      localStorage.removeItem(wheelStorageKey);
      localStorage.removeItem(WHEEL_STORAGE_KEY_LEGACY);
      localStorage.removeItem(gexStorageKey);
      localStorage.removeItem(GEX_STORAGE_KEY_LEGACY);
      localStorage.removeItem('qw_wheel_at_broker_connect');
      localStorage.removeItem('qw_onboarding_broker_intent');
      localStorage.removeItem('qw_onboarding_manual_intent');
      localStorage.removeItem('qw_onboarding_auto_manual');
      localStorage.removeItem('qw_gex_tour_pending');
    } catch {}
  }, [wheelStorageKey, gexStorageKey]);

  // Persist wheel onboarding state on every stage change
  useEffect(() => {
    if (wheelOnboardingActive) {
      saveWheelState(wheelStage, wheelCompletedStages, true);
    }
  }, [wheelOnboardingActive, wheelStage, wheelCompletedStages, saveWheelState]);

  // Persist GEX onboarding state on every stage change
  useEffect(() => {
    if (gexOnboardingActive) {
      saveGexState(gexStage, gexCompletedStages, true);
    }
  }, [gexOnboardingActive, gexStage, gexCompletedStages, saveGexState]);

  // Persist when broker-connect modal is shown (end of wheel tour)
  useEffect(() => {
    if (showBrokerConnect) {
      saveWheelState(wheelStage, wheelCompletedStages, true);
      try { localStorage.setItem('qw_wheel_at_broker_connect', '1'); } catch {}
    }
  }, [showBrokerConnect, wheelStage, wheelCompletedStages, saveWheelState]);

  // Prevent cross-account onboarding state leaks in shared browsers.
  useEffect(() => {
    if (sessionStatus === 'loading') return;
    try {
      const currentUser = session?.user?.id || 'anon';
      const previousUser = localStorage.getItem(lastOnboardingUserKey);
      if (previousUser && previousUser !== currentUser) {
        localStorage.removeItem(`qw_wheel_onboarding_state:${previousUser}`);
        localStorage.removeItem(`qw_gex_onboarding_state:${previousUser}`);
        localStorage.removeItem(WHEEL_STORAGE_KEY_LEGACY);
        localStorage.removeItem(GEX_STORAGE_KEY_LEGACY);
      }
      localStorage.setItem(lastOnboardingUserKey, currentUser);
      restoredRef.current = false;
    } catch {}
  }, [sessionStatus, session?.user?.id, lastOnboardingUserKey]);

  // Restore onboarding state from localStorage on mount
  useEffect(() => {
    if (restoredRef.current) return;
    if (!hasCheckedOnboarding || !subscriptionLoaded) return;
    if (wheelOnboardingActive || gexOnboardingActive) return;
    restoredRef.current = true;

    const isPaidOrSpecial = subscriptionLoaded && (!isFreeUser || isSpecialUser);

    try {
      const wheelRaw = getScopedState(
        wheelStorageKey,
        WHEEL_STORAGE_KEY_LEGACY,
        onboardingScope === 'anon'
      );
      const wheelAtBroker = localStorage.getItem('qw_wheel_at_broker_connect');
      // GEX-only subscribers never resume a (pre-purchase) wheel tour: the
      // Wheel is not in their plan - they get the demo + PRO banner instead.
      if (wheelRaw && !isGexOnly) {
        const saved = JSON.parse(wheelRaw);
        if (saved.active) {
          const restoredStage = sanitizeWheelStage(saved.stage);
          setWheelOnboardingActive(true);
          setWheelFlow('wheel');
          setWheelStage(restoredStage);
          setWheelCompletedStages(saved.completedStages || []);
          if (wheelAtBroker) {
            localStorage.removeItem('qw_wheel_at_broker_connect');
            setShowBrokerConnect(true);
          } else if (!isPaidOrSpecial) {
            const target = stageHref(restoredStage);
            if (!isSameAppPath(pathname || '', target)) {
              setShowTourGuard(true);
              router.replace(target);
            }
          }
          return;
        }
      }

      const gexRaw = getScopedState(
        gexStorageKey,
        GEX_STORAGE_KEY_LEGACY,
        onboardingScope === 'anon'
      );
      if (gexRaw) {
        const saved = JSON.parse(gexRaw);
        if (saved.active) {
          setGexOnboardingActive(true);
          setGexStage(saved.stage);
          setGexCompletedStages(saved.completedStages || []);
          if (!isPaidOrSpecial) {
            const target = gexStageHref(saved.stage);
            if (!pathname?.startsWith(target)) {
              router.push(target);
            }
          }
          return;
        }
      }
    } catch {}

    // Check if user came back from login with broker intent
    try {
      const brokerIntent = localStorage.getItem('qw_onboarding_broker_intent');
      if (brokerIntent && session?.user?.id) {
        localStorage.removeItem('qw_onboarding_broker_intent');
        setShowBrokerConnect(true);
      }
    } catch {}

    // Check if user came back from login with manual position intent
    try {
      const manualIntent = localStorage.getItem('qw_onboarding_manual_intent');
      if (manualIntent && session?.user?.id) {
        localStorage.removeItem('qw_onboarding_manual_intent');
        setShowBrokerConnect(true);
        localStorage.setItem('qw_onboarding_auto_manual', '1');
      }
    } catch {}
  }, [hasCheckedOnboarding, subscriptionLoaded, wheelOnboardingActive, gexOnboardingActive, router, session?.user?.id, getScopedState, wheelStorageKey, gexStorageKey, onboardingScope, isFreeUser, isGexOnly, isSpecialUser, clearAllTourState, pathname]); // pathname intentionally read as snapshot, restoredRef guards re-entry

  // Toggle body class for dimming overlay (wheel only -- GEX tour uses real pages)
  useEffect(() => {
    if (wheelOnboardingActive) {
      document.body.classList.add('onboarding-active');
    } else {
      document.body.classList.remove('onboarding-active');
    }
    return () => document.body.classList.remove('onboarding-active');
  }, [wheelOnboardingActive]);

  // Auto-switch between Pro and GEX tours when user navigates across sections
  useEffect(() => {
    if (!pathname) return;
    if (!wheelOnboardingActive && !gexOnboardingActive && !wheelTourCompleted && !gexTourCompleted) return;
    const onGex = pathname.startsWith('/dashboard/gex');
    const onWheel = pathname.startsWith('/dashboard/wheel');

    // Free Wheel-tour users are returned to their current step by the path guard.
    if (wheelOnboardingActive && isFreeUser && !isSpecialUser && onGex) return;

    // Wheel active, user navigated to GEX → pause wheel, resume/start GEX
    if ((wheelOnboardingActive || wheelTourCompleted) && onGex && !showBrokerConnect && !showOnboardingPricing) {
      saveWheelState(wheelStage, wheelCompletedStages, true);
      setWheelOnboardingActive(false);
      setWheelTourCompleted(false);
      try {
        const gexRaw = getScopedState(
          gexStorageKey,
          GEX_STORAGE_KEY_LEGACY,
          onboardingScope === 'anon'
        );
        if (gexRaw) {
          const saved = JSON.parse(gexRaw);
          if (saved.active) {
            setGexOnboardingActive(true);
            setGexStage(saved.stage);
            setGexCompletedStages(saved.completedStages || []);
            if (saved.completed) setGexTourCompleted(true);
            return;
          }
        }
      } catch {}
      setGexOnboardingActive(true);
      setGexStage(firstGexStage);
      setGexCompletedStages([]);
      setGexTourCompleted(false);
      setShowGexIntro(true);
      saveGexState(firstGexStage, [], true);
    }

    // GEX active, user navigated to Wheel → pause GEX, resume/start wheel
    if ((gexOnboardingActive || gexTourCompleted) && onWheel) {
      if (!gexTourCompleted) {
        saveGexState(gexStage, gexCompletedStages, true);
      }
      setGexOnboardingActive(false);
      setGexTourCompleted(false);
      try {
        const wheelRaw = getScopedState(
          wheelStorageKey,
          WHEEL_STORAGE_KEY_LEGACY,
          onboardingScope === 'anon'
        );
        if (wheelRaw) {
          const saved = JSON.parse(wheelRaw);
          if (saved.active) {
            setWheelOnboardingActive(true);
            setWheelFlow('wheel');
            setWheelStage(sanitizeWheelStage(saved.stage));
            setWheelCompletedStages(saved.completedStages || []);
            if (saved.completed) setWheelTourCompleted(true);
            return;
          }
        }
      } catch {}
      setWheelOnboardingActive(true);
      setWheelFlow('wheel');
      setWheelStage('find-deals');
      setWheelCompletedStages([]);
      setWheelTourCompleted(false);
      setShowWheelIntro(true);
      saveWheelState('find-deals', [], true);
    }
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check subscription status for expired users + track free user.
  // Wait for session to fully settle before acting to avoid multiple context
  // value changes that cascade re-renders through the whole tree.
  useEffect(() => {
    if (sessionStatus === 'loading') return;
    if (sessionStatus === 'unauthenticated') {
      // Only downgrade to the free/sandbox defaults when the user is
      // verifiably logged out; keep last-known state on connection loss
      // (reconnectNonce re-runs this effect once the server is back).
      let stale = false;
      void confirmLoggedOut().then((loggedOut) => {
        if (stale || !loggedOut) return;
        setIsFreeUser(true);
        setHadPreviousSubscription(false);
        setSubscriptionLoaded(true);
        setIsSpecialUser(false);
      });
      return () => { stale = true; };
    }
    if (!session?.user?.id) return;
    let cancelled = false;
    let attempts = 0;
    const maxRetries = 2;

    const fetchStatus = () => {
      attempts++;
      fetch('/api/user/subscription-status', { cache: 'no-store' })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (cancelled) return;
          if (!data) {
            // Fail-open for authenticated users to avoid accidental lockouts on transient API failures.
            setIsFreeUser(false);
            setIsGexOnly(false);
            setIsSpecialUser(false);
            setHadPreviousSubscription(false);
            setSubscriptionLoaded(true);
            return;
          }
          const special = !!data.isSpecialUser || !!data.hasBypassAccess;
          setIsSpecialUser(special);
          setIsFreeUser(special ? false : !data.isPaid);
          setIsGexOnly(!!data.isGexOnly);
          setHadPreviousSubscription(!!data.hadPreviousSubscription);
          setSubscriptionLoaded(true);
          if ((data.status === 'EXPIRED_STRIPE' || data.status === 'EXPIRED_NO_CC') && data.hadPreviousSubscription) {
            setShowExpiredGate(true);
          }
        })
        .catch(() => {
          if (cancelled) return;
          if (attempts < maxRetries) {
            setTimeout(fetchStatus, attempts * 2000);
            return;
          }
          // Exhausted retries - likely a lost connection. Surface the banner
          // and start recovery polling, then fail-open for authenticated
          // users so nothing locks up in the meantime.
          reportServerUnreachable();
          setIsFreeUser(false);
          setIsGexOnly(false);
          setIsSpecialUser(false);
          setHadPreviousSubscription(false);
          setSubscriptionLoaded(true);
        });
    };

    fetchStatus();
    return () => { cancelled = true; };
  }, [sessionStatus, session?.user?.id, reconnectNonce]);

  // Re-fetch subscription when user returns to tab (e.g. after Stripe redirect)
  useEffect(() => {
    if (sessionStatus !== 'authenticated' || !session?.user?.id) return;
    const userId = session.user.id;
    function handleVisibilityChange() {
      if (document.visibilityState !== 'visible') return;
      fetch('/api/user/subscription-status', { cache: 'no-store' })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (!data) return;
          const special = !!data.isSpecialUser || !!data.hasBypassAccess;
          setIsSpecialUser(special);
          setIsFreeUser(special ? false : !data.isPaid);
          setIsGexOnly(!!data.isGexOnly);
          setHadPreviousSubscription(!!data.hadPreviousSubscription);
        })
        .catch(() => {});
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [sessionStatus, session?.user?.id]);

  // Re-fetch subscription after returning from Stripe checkout (same-tab redirect).
  // The initial subscription fetch may have already completed with stale data before
  // the Stripe webhook updated the DB, so we refetch with escalating delays.
  // Stops early once the user is confirmed paid.
  useEffect(() => {
    if (subscriptionRefetchedForStripeReturn.current) return;
    if (sessionStatus !== 'authenticated' || !session?.user?.id) return;
    const params = new URLSearchParams(window.location.search);
    if (!params.has('success') && !params.has('upgraded') && !params.has('subscribed')) return;
    subscriptionRefetchedForStripeReturn.current = true;
    let confirmed = false;
    const refetch = () => {
      if (confirmed) return;
      fetch('/api/user/subscription-status', { cache: 'no-store' })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (!data) return;
          const special = !!data.isSpecialUser || !!data.hasBypassAccess;
          setIsSpecialUser(special);
          setIsFreeUser(special ? false : !data.isPaid);
          setIsGexOnly(!!data.isGexOnly);
          setHadPreviousSubscription(!!data.hadPreviousSubscription);
          if (data.isPaid || special) {
            confirmed = true;
            setShowExpiredGate(false);
          }
        })
        .catch(() => {});
    };
    // Webhook may arrive with variable delay - retry at 2s, 5s, 10s, 18s.
    const delays = [2000, 4000, 8000, 10000];
    const timers = delays.map(ms => setTimeout(refetch, ms));
    return () => timers.forEach(clearTimeout);
  }, [sessionStatus, session?.user?.id]);

  // When a free user transitions to paid mid-session (e.g. after Stripe checkout),
  // fully end any active onboarding tour so the user is never locked into it.
  useEffect(() => {
    if (!subscriptionLoaded || isFreeUser) return;
    setShowOnboardingPricing(false);
    setShowExpiredGate(false);

    if (wheelOnboardingActive) {
      setWheelOnboardingActive(false);
      setWheelFlow(null);
      setWheelStage('find-deals');
      setWheelCompletedStages([]);
      setShowBrokerConnect(false);
      clearWheelState();
    }
    if (gexOnboardingActive) {
      setGexOnboardingActive(false);
      setGexCompletedStages([]);
      setShowGexIntro(false);
      clearGexState();
    }

    setHasCompletedOnboarding(true);
    clearAllTourState();

    if (session?.user?.id) {
      fetch('/api/user/onboarding', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ completed: true }),
      }).catch(() => {});
    }
  }, [subscriptionLoaded, isFreeUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check if user has any broker/paper/csv connection (enables real data for free users)
  useEffect(() => {
    if (sessionStatus !== 'authenticated' || !session?.user?.id) return;
    Promise.all([
      fetch('/api/broker/connections').then(r => r.ok ? r.json() : null).catch(() => null),
      fetch('/api/broker/paper/status').then(r => r.ok ? r.json() : null).catch(() => null),
    ]).then(([connData, paperData]) => {
      const auths = connData?.authorizations || [];
      const ibkr = connData?.ibkrConnections || [];
      const hasPaper = !!paperData?.account;
      setHasBrokerConnected(auths.length > 0 || ibkr.length > 0 || hasPaper);
    }).catch(() => {});
  }, [sessionStatus, session?.user?.id]);

  const completeWheelOnboarding = React.useCallback(() => {
    setWheelOnboardingActive(false);
    setWheelFlow(null);
    setWheelStage('find-deals');
    setWheelCompletedStages([]);
    setShowBrokerConnect(false);
    setShowOnboardingPricing(false);
    setHasCompletedOnboarding(true);
    clearWheelState();
    try { localStorage.removeItem('qw_wheel_at_broker_connect'); } catch {}
    if (session?.user?.id) {
      fetch('/api/user/onboarding', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ completed: true }),
      }).catch(() => {});
    } else {
      try { localStorage.setItem('qw_sandbox_onboarding_seen', '1'); } catch {}
    }
  }, [session?.user?.id, clearWheelState]);

  const advanceWheelStage = React.useCallback(() => {
    const progressPersisted = wheelProgressRef.current.stage(wheelStage as WheelTourProgressStage);
    setWheelCompletedStages(prev => prev.includes(wheelStage) ? prev : [...prev, wheelStage]);
    const idx = stageIndex(wheelStage);
    if (idx < WHEEL_STAGES.length - 1) {
      const next = WHEEL_STAGES[idx + 1];
      setWheelStage(next);
      setTimeout(() => router.push(stageHref(next)), 100);
    } else {
      setWheelCompletedStages(prev => prev.includes('suggested-actions') ? prev : [...prev, 'suggested-actions' as WheelOnboardingStage]);
      const creditEligibleCompletion = isFreeUser && !isSpecialUser;
      if (creditEligibleCompletion) {
        void progressPersisted.then((persisted) => persisted && issueWheelTourCreditProof()).then((issued) => {
          if (issued) { markWheelTourCreditEligible(); void requestWheelTourCredit(); }
        });
      }

      if (!wheelTourBrokerFirst) {
        completeWheelOnboarding();
        if (creditEligibleCompletion) setShowOnboardingPricing(true);
        return;
      }

      // Paid users who already have a broker connected gain nothing from the
      // connect-broker gate (it pitches a $1 trial). Skip it and finish the tour
      // so the blinking "Complete Tour" button isn't blocked by the modal.
      if ((!isFreeUser || isSpecialUser) && hasBrokerConnected) {
        completeWheelOnboarding();
        return;
      }
      setShowBrokerConnect(true);
    }
  }, [wheelStage, router, isFreeUser, isSpecialUser, hasBrokerConnected, completeWheelOnboarding, wheelTourBrokerFirst]);

  const startWheelOnboarding = React.useCallback((flow: 'wheel' | 'gex-first', quickRecap = false) => {
    void wheelProgressRef.current.start();
    setWheelOnboardingActive(true);
    setWheelTourCompleted(false);
    setWheelFlow(flow);
    setWheelStage('review');
    setWheelCompletedStages([]);
    setWheelQuickRecap(quickRecap);
    setShowBrokerConnect(false);
    setShowOnboardingPricing(false);
    setShowWheelIntro(true);
    try { localStorage.removeItem('qw_wheel_at_broker_connect'); } catch {}
    saveWheelState('review', [], true);
    router.push('/dashboard/wheel/review');
    trackClientEvent({ name: 'tour_started', params: { tour_type: 'pro', flow, quickRecap }, userId: session?.user?.id ?? undefined });
  }, [router, session?.user?.id, saveWheelState]);

  const dismissWheelIntro = React.useCallback(() => {
    setShowWheelIntro(false);
  }, []);

  // Pricing is shown to everyone (including anonymous users); the plan CTAs
  // encode the trial intent into the login flow so post-login goes straight to Stripe.
  const guardedSetShowPricing = React.useCallback((show: boolean) => {
    setShowOnboardingPricing(show);
  }, []);

  // --- GEX Onboarding Callbacks ---
  const completeGexOnboardingFn = React.useCallback(() => {
    setGexOnboardingActive(false);
    setGexTourCompleted(true);
    setShowGexIntro(false);
    setHasCompletedOnboarding(true);
    if (isFreeUser && !isSpecialUser) {
      guardedSetShowPricing(true);
    }
    const finalStage = gexStages[gexStages.length - 1];
    try { localStorage.setItem(gexStorageKey, JSON.stringify({ stage: finalStage, completedStages: [...gexStages], active: false, completed: true })); } catch {}
    try { localStorage.removeItem('qw_wheel_at_broker_connect'); } catch {}
    if (session?.user?.id) {
      fetch('/api/user/onboarding', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ completed: true }),
      }).catch(() => {});
    } else {
      try { localStorage.setItem('qw_sandbox_onboarding_seen', '1'); } catch {}
    }
  }, [session?.user?.id, gexStorageKey, guardedSetShowPricing, isFreeUser, isSpecialUser, gexStages]);

  const advanceGexStage = React.useCallback(() => {
    setGexCompletedStages(prev => prev.includes(gexStage) ? prev : [...prev, gexStage]);
    const idx = gexStages.indexOf(gexStage);
    if (idx < gexStages.length - 1) {
      const next = gexStages[idx + 1];
      setGexStage(next);
      setTimeout(() => router.push(gexStageHref(next)), 100);
    } else {
      const finalStage = gexStages[gexStages.length - 1];
      setGexCompletedStages(prev => prev.includes(finalStage) ? prev : [...prev, finalStage]);
      completeGexOnboardingFn();
    }
  }, [gexStage, router, completeGexOnboardingFn, gexStages]);

  const goBackWheelStage = React.useCallback(() => {
    const idx = stageIndex(wheelStage);
    if (idx <= 0) return;
    const prev = WHEEL_STAGES[idx - 1];
    setWheelCompletedStages(cs => cs.filter(s => s !== wheelStage && s !== prev));
    setWheelStage(prev);
    setTimeout(() => router.push(stageHref(prev)), 100);
  }, [wheelStage, router]);

  const goBackGexStage = React.useCallback(() => {
    const idx = gexStages.indexOf(gexStage);
    if (idx <= 0) return;
    const prev = gexStages[idx - 1];
    setGexCompletedStages(cs => cs.filter(s => s !== gexStage && s !== prev));
    setGexStage(prev);
    setTimeout(() => router.push(gexStageHref(prev)), 100);
  }, [gexStage, router, gexStages]);

  const startGexOnboarding = React.useCallback(() => {
    setWheelOnboardingActive(false);
    setGexOnboardingActive(true);
    // Reset the completed flag so a restarted tour shows steps again instead
    // of the "GEX Tour Complete" panel with only an Upgrade button.
    setGexTourCompleted(false);
    setGexStage(firstGexStage);
    setGexCompletedStages([]);
    setShowGexIntro(true);
    saveGexState(firstGexStage, [], true);
    router.push(gexStageHref(firstGexStage));
    trackClientEvent({ name: 'tour_started', params: { tour_type: 'gex' }, userId: session?.user?.id ?? undefined });
  }, [router, session?.user?.id, saveGexState, firstGexStage]);

  const dismissGexIntro = React.useCallback(() => {
    setShowGexIntro(false);
  }, []);

  // --- Mid-tour switching (user picked the wrong tour in the starter) ---
  // Wheel -> GEX must flip state here (not via the pathname effect): the free
  // user path guard blocks navigation away from the wheel while the wheel
  // tour is active, so deactivate it first. Wheel progress stays resumable.
  const switchToGexTour = React.useCallback(() => {
    saveWheelState(wheelStage, wheelCompletedStages, true);
    setWheelOnboardingActive(false);
    setWheelTourCompleted(false);
    try {
      const gexRaw = getScopedState(
        gexStorageKey,
        GEX_STORAGE_KEY_LEGACY,
        onboardingScope === 'anon'
      );
      if (gexRaw) {
        const saved = JSON.parse(gexRaw);
        if (saved.active) {
          setGexOnboardingActive(true);
          setGexStage(saved.stage);
          setGexCompletedStages(saved.completedStages || []);
          if (saved.completed) setGexTourCompleted(true);
          router.push(gexStageHref(saved.stage));
          return;
        }
      }
    } catch {}
    startGexOnboarding();
  }, [wheelStage, wheelCompletedStages, saveWheelState, getScopedState, gexStorageKey, onboardingScope, router, startGexOnboarding]);

  // GEX -> Wheel only navigates; the pathname auto-switch effect pauses the
  // GEX tour and resumes (or starts) the wheel tour once the wheel route is
  // active. Activating the wheel tour before landing there would trip its
  // path guard mid-navigation.
  const switchToWheelTour = React.useCallback(() => {
    let target = stageHref('find-deals');
    try {
      const wheelRaw = getScopedState(
        wheelStorageKey,
        WHEEL_STORAGE_KEY_LEGACY,
        onboardingScope === 'anon'
      );
      if (wheelRaw) {
        const saved = JSON.parse(wheelRaw);
        if (saved.active) target = stageHref(sanitizeWheelStage(saved.stage));
      }
    } catch {}
    router.push(target);
  }, [getScopedState, wheelStorageKey, onboardingScope, router]);

  const onboardingModeValue: OnboardingModeContextType = React.useMemo(() => ({
    isOnboardingMode: wheelOnboardingActive || gexOnboardingActive,
    flow: wheelFlow,
    wheelStage,
    completedStages: wheelCompletedStages,
    advanceStage: advanceWheelStage,
    completeOnboarding: completeWheelOnboarding,
    isQuickRecap: wheelQuickRecap,
    showBrokerConnect,
    setShowBrokerConnect,
    showPricing: showOnboardingPricing,
    setShowPricing: guardedSetShowPricing,
    showIntro: showWheelIntro,
    dismissIntro: dismissWheelIntro,
    isFreeUser,
    isGexOnly,
    isSpecialUser,
    subscriptionLoaded,
    hadPreviousSubscription,
    isGexOnboarding: gexOnboardingActive,
    gexStage,
    gexCompletedStages,
    advanceGexStage,
    completeGexOnboarding: completeGexOnboardingFn,
    showGexIntro,
    dismissGexIntro,
    showTourGuard,
    setShowTourGuard,
    goBackStage: goBackWheelStage,
    goBackGexStage,
    switchToGexTour,
    switchToWheelTour,
    gexTourCompleted,
    wheelTourCompleted,
    hasBrokerConnected,
    isActivelyInTour: wheelOnboardingActive || gexOnboardingActive,
  }), [wheelOnboardingActive, wheelFlow, wheelStage, wheelCompletedStages, advanceWheelStage, completeWheelOnboarding, wheelQuickRecap, showBrokerConnect, showOnboardingPricing, showWheelIntro, dismissWheelIntro, isFreeUser, isGexOnly, isSpecialUser, subscriptionLoaded, hadPreviousSubscription, gexOnboardingActive, gexStage, gexCompletedStages, advanceGexStage, completeGexOnboardingFn, showGexIntro, dismissGexIntro, showTourGuard, goBackWheelStage, goBackGexStage, switchToGexTour, switchToWheelTour, gexTourCompleted, wheelTourCompleted, hasBrokerConnected, guardedSetShowPricing]);

  // isFreeUser is true for anonymous visitors and logged-in non-customers;
  // paying/special customers are never forced into the tour picker.
  // Never open it while the connection is down: loss shows a banner instead.
  const showInterestsModal = connectionUp && hasCheckedOnboarding && subscriptionLoaded
    && isFreeUser
    && !hasCompletedOnboarding
    && (onboardingInterests.length === 0 || showPreferencesStep);

  const cleanupHighlights = () => {
    document.querySelectorAll('.shepherd-highlight').forEach((el) => el.classList.remove('shepherd-highlight'));
  };

  // Check if the user has completed onboarding and load interests.
  // If user just registered and has sandbox interests in localStorage, migrate them to the API.
  // Skip the API call entirely when unauthenticated (sandbox) to avoid 401.
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      try {
        if (sessionStatus === 'unauthenticated' || !session?.user?.id) {
          // With the server unreachable, a logged-in user transiently looks
          // unauthenticated. Don't reset onboarding state to the sandbox
          // defaults (which would pop the interests modal / start a tour)
          // unless the logout is confirmed; retry on reconnect.
          if (!(await confirmLoggedOut())) return;
          const sandboxSeen = typeof window !== 'undefined' && localStorage.getItem('qw_sandbox_onboarding_seen');
          const sandboxInterests = getSandboxInterests();
          setHasCompletedOnboarding(!!sandboxSeen);
          setOnboardingInterests(sandboxInterests);
          setHasCheckedOnboarding(true);
          return;
        }
        const urlParams = new URLSearchParams(window.location.search);
        const registrationPage = urlParams.get('registration_page');
        let apiUrl = '/api/user/onboarding';
        if (registrationPage) {
          apiUrl += `?registration_page=${encodeURIComponent(registrationPage)}`;
        }
        const response = await fetch(apiUrl);
        if (response.ok) {
          const data = await response.json();
          const serverInterests = Array.isArray(data.onboardingInterests) ? data.onboardingInterests : [];
          const serverCompleted = data.hasCompletedOnboarding ?? true;

          if (serverCompleted || serverInterests.length > 0) {
            setHasCompletedOnboarding(serverCompleted);
            setOnboardingInterests(serverInterests);
            setHasCheckedOnboarding(true);
            return;
          }

          const sandboxInterests = getSandboxInterests();
          if (sandboxInterests.length > 0) {
            const migrateRes = await fetch('/api/user/onboarding', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ interests: sandboxInterests }),
            });
            if (migrateRes.ok) {
              clearSandboxOnboarding();
              setOnboardingInterests(sandboxInterests);
              setHasCompletedOnboarding(true);
            } else {
              setHasCompletedOnboarding(serverCompleted);
              setOnboardingInterests(serverInterests);
            }
          } else {
            setHasCompletedOnboarding(serverCompleted);
            setOnboardingInterests(serverInterests);
          }
        } else {
          setHasCompletedOnboarding(true);
          setOnboardingInterests([]);
        }
      } catch (error) {
        console.error('Error checking onboarding status:', error);
        reportServerUnreachable();
      } finally {
        setHasCheckedOnboarding(true);
      }
    };

    if (sessionStatus === 'loading') return;
    checkOnboardingStatus();
  }, [sessionStatus, session?.user?.id, reconnectNonce]);

  // Resume GEX tour after login if the pending flag was set before auth redirect
  useEffect(() => {
    if (!hasCheckedOnboarding || !subscriptionLoaded) return;
    if (gexOnboardingActive || wheelOnboardingActive) return;
    try {
      const pending = localStorage.getItem('qw_gex_tour_pending');
      if (pending) {
        localStorage.removeItem('qw_gex_tour_pending');
        startGexOnboarding();
      }
    } catch {}
  }, [hasCheckedOnboarding, subscriptionLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Add/remove body class to hide Shepherd tooltip when using custom modal
  useEffect(() => {
    if (isActive) {
      document.body.classList.add('tour-custom-modal');
    } else {
      document.body.classList.remove('tour-custom-modal');
      setCurrentStepInfo(null);
    }
    return () => document.body.classList.remove('tour-custom-modal');
  }, [isActive]);

  // NO auto-start on page refresh. Tour only starts from:
  // 1. Interests modal onComplete callback (first onboarding)
  // 2. User clicking Start/Replay in the hub widget

  // Helper function to ensure mobile sidebar is open
  const ensureMobileSidebarIsOpen = (): Promise<boolean> => {
    return new Promise((resolve) => {
      const isMobile = window.innerWidth < 640;
      
      if (!isMobile) {
        resolve(false);
        return;
      }
      
      // console.log('Ensuring mobile sidebar is open');
      
      // Check if sidebar is already open
      const openSheet = document.querySelector('[role="dialog"][data-state="open"]');
      if (openSheet) {
        // console.log('Mobile sidebar already open');
        resolve(true);
        return;
      }
      
      // Need to open the mobile sidebar
      const mobileButton = document.querySelector('[data-mobile-menu]') ||
                          document.querySelector('button[aria-label*="menu"]') ||
                          document.querySelector('button[aria-label*="navigation"]');
      if (mobileButton) {
        // console.log('Opening mobile sidebar for tour step');
        // Store the element for later reference
        (window as any).__tourMobileButtonClicked = true;
        (mobileButton as HTMLElement).click();
        
        // Wait for sidebar to open
        const observer = new MutationObserver((mutations, obs) => {
          const sidebarSheet = document.querySelector('[role="dialog"][data-state="open"]');
          if (sidebarSheet) {
            // console.log('Mobile sidebar opened successfully');
            obs.disconnect();
            resolve(true);
          }
        });
        
        observer.observe(document.body, { childList: true, subtree: true });
        
        // Fallback timeout
        setTimeout(() => {
          observer.disconnect();
          // console.log('Mobile sidebar open timeout reached');
          resolve(true);
        }, 1000);
      } else {
        // console.log('Mobile menu button not found');
        resolve(false);
      }
    });
  };

  // Helper function to ensure the sidebar is expanded
  const ensureSidebarIsExpanded = (): boolean | Promise<boolean> => {
    // Prevent multiple simultaneous calls
    if (window.__sidebarExpansionInProgress) {
      // console.log('Sidebar expansion already in progress, skipping');
      return false;
    }

    // Check if on mobile with collapsed sidebar
    const isMobile = window.innerWidth < 640;
    
    if (isMobile) {
      // console.log('Mobile detected, attempting to open sidebar sheet');
      
      // First check if sidebar is already open to prevent closing it
      const openSheet = document.querySelector('[role="dialog"][data-state="open"]');
      if (openSheet) {
        // console.log('Sidebar already open on mobile');
        return true;
      }
      
      // Force-click the mobile menu button
      const mobileButton = document.querySelector('[data-mobile-menu]') ||
                          document.querySelector('button[aria-label*="menu"]') ||
                          document.querySelector('button[aria-label*="navigation"]');
      if (mobileButton) {
        // console.log('Found mobile menu button, clicking it');
        // Store the element for later reference
        (window as any).__tourMobileButtonClicked = true;
        (mobileButton as HTMLElement).click();
        return true;
      } else {
        // console.log('Mobile menu button not found');
      }
    } else {
      // console.log('Desktop mode, checking sidebar state');
      
      // Set flag to prevent multiple calls
      window.__sidebarExpansionInProgress = true;
      
      // More aggressive approach for desktop - find any sidebar-related elements
      const possibleSidebarToggles = [
        '[data-sidebar-toggle]',
        '[data-expand-sidebar]', 
        'button[aria-label*="sidebar"]',
        'button[aria-label*="menu"]',
        'button[aria-label*="navigation"]',
        '.sidebar-toggle',
        '[data-testid*="sidebar"]',
        '[data-testid*="menu"]'
      ];
      
      let sidebarToggle: Element | null = null;
      for (const selector of possibleSidebarToggles) {
        sidebarToggle = document.querySelector(selector);
        if (sidebarToggle) {
          // console.log(`Found sidebar toggle with selector: ${selector}`);
          break;
        }
      }
      
      // Check if sidebar is collapsed - try multiple approaches
      const sidebar = document.querySelector('[data-sidebar]') || 
                     document.querySelector('.sidebar') ||
                     document.querySelector('aside') ||
                     document.querySelector('nav[role="navigation"]');
      
      if (sidebar && sidebarToggle) {
        // Multiple ways to detect if sidebar is collapsed
        const sidebarElement = sidebar as HTMLElement;
        
        // Wait a moment to ensure any ongoing animations have settled
        return new Promise<boolean>((resolve) => {
          setTimeout(() => {
            const isCollapsed = sidebar.classList.contains('collapsed') || 
                               sidebar.classList.contains('hidden') ||
                               sidebar.classList.contains('closed') ||
                               sidebar.getAttribute('data-state') === 'collapsed' ||
                               sidebar.getAttribute('data-collapsed') === 'true' ||
                               getComputedStyle(sidebar).display === 'none' ||
                               sidebarElement.offsetWidth < 100; // Assume collapsed if very narrow
            
            if (isCollapsed) {
              // console.log('Desktop sidebar appears collapsed, expanding it');
              (sidebarToggle as HTMLElement).click();
              
              // Wait longer for the sidebar to expand and clear the flag
              setTimeout(() => {
                // console.log('Sidebar expansion complete');
                window.__sidebarExpansionInProgress = false;
                resolve(false);
              }, 500); // Increased wait time
            } else {
              // console.log('Desktop sidebar already appears expanded');
              window.__sidebarExpansionInProgress = false;
              resolve(false);
            }
          }, 100); // Initial delay to let any animations settle
        });
      } else {
        // console.log('Could not find sidebar or toggle elements');
        window.__sidebarExpansionInProgress = false;
      }
    }
    
    return isMobile;
  };

  // Highlight an element (uses component-level cleanupHighlights)
  const highlightElement = (selector: string) => {
    // console.log(`Highlighting element with selector: ${selector}`);
    
    // First remove any existing highlights
    cleanupHighlights();
    
    // Try to find element by data attribute first for more precise targeting
    const targetSelector = selector.replace('.', '');
    let element = document.querySelector(`[data-shepherd-highlight-target="${targetSelector}"]`);
    
    // Fall back to class selector if data attribute not found
    if (!element) {
      // console.log(`No element found with data-shepherd-highlight-target="${targetSelector}", trying class selector`);
      element = document.querySelector(selector);
    }
    
    // Try by text content as last resort
    if (!element && (selector.includes('watchlist') || selector.includes('Watchlist'))) {
      // console.log('Looking for watchlist element by text content');
      const allElements = document.querySelectorAll('a, div, button');
      for (let i = 0; i < allElements.length; i++) {
        if (allElements[i].textContent?.toLowerCase().includes('watchlist')) {
          element = allElements[i];
          break;
        }
      }
    }
    
    if (element) {
      // console.log('Element found, adding highlight class');
      element.classList.add('shepherd-highlight');
      // Scroll element into view if needed
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      // console.log(`Failed to find element with selector: ${selector}`);
    }
    
    return element;
  };

  /** Return the first element matching `selector` that is actually visible (not inside a hidden parent). */
  const queryVisible = (selector: string): Element | null => {
    const all = document.querySelectorAll(selector);
    for (let i = 0; i < all.length; i++) {
      const el = all[i] as HTMLElement;
      if (el.offsetParent !== null || el.getClientRects().length > 0) return el;
    }
    return all.length > 0 ? all[0] : null; // fall back to first match if nothing visible
  };

  const highlightForSubStep = (subStep: TourSubStep) => {
    cleanupHighlights();
    let el = queryVisible(`[data-shepherd-highlight-target="${subStep.highlightTarget}"]`);
    if (!el && subStep.highlightFallbackTarget) {
      el = queryVisible(`[data-shepherd-highlight-target="${subStep.highlightFallbackTarget}"]`);
    }
    if (el) {
      el.classList.add('shepherd-highlight');
      (el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const attachCompletionListener = (subStep: TourSubStep, onComplete: () => void) => {
    if (moduleListenerCleanupRef.current) {
      moduleListenerCleanupRef.current();
      moduleListenerCleanupRef.current = null;
    }
    if (subStep.completionEvent === 'manual' || subStep.completionEvent === 'custom') return;
    const selector = subStep.completionSelector || `[data-shepherd-highlight-target="${subStep.highlightTarget}"]`;
    const el = queryVisible(selector);
    if (!el) return;
    const handler = () => {
      if (subStep.completionCheck && !subStep.completionCheck()) return;
      if (moduleListenerCleanupRef.current) {
        moduleListenerCleanupRef.current();
        moduleListenerCleanupRef.current = null;
      }
      onComplete();
    };
    if (subStep.completionEvent === 'click') {
      el.addEventListener('click', handler, { once: true, capture: true });
      moduleListenerCleanupRef.current = () => el.removeEventListener('click', handler, { capture: true });
    } else if (subStep.completionEvent === 'change') {
      const input = el instanceof HTMLInputElement ? el : el.querySelector('input');
      if (input) {
        const changeHandler = () => {
          if (subStep.completionCheck && !subStep.completionCheck()) return;
          if (moduleListenerCleanupRef.current) {
            moduleListenerCleanupRef.current();
            moduleListenerCleanupRef.current = null;
          }
          onComplete();
        };
        input.addEventListener('change', changeHandler, { once: true });
        input.addEventListener('blur', changeHandler, { once: true });
        moduleListenerCleanupRef.current = () => {
          input.removeEventListener('change', changeHandler);
          input.removeEventListener('blur', changeHandler);
        };
      }
    }
  };

  const completeModule = (moduleDef: TourModuleDef) => {
    if (moduleListenerCleanupRef.current) {
      moduleListenerCleanupRef.current();
      moduleListenerCleanupRef.current = null;
    }
    cleanupHighlights();
    if (typeof window !== 'undefined') (window as unknown as { __tourSidebarLocked?: boolean }).__tourSidebarLocked = false;
    setCompletedModuleIds((prev) => prev.includes(moduleDef.id) ? prev : [...prev, moduleDef.id]);
    setActiveModuleId(null);
    setModuleSubStepIndex(0);
    setModuleCompletedSubSteps([]);
    setCurrentStepInfo(null);
    setIsActive(false);

    // GEX-first flow: after GEX module completes, chain into Wheel onboarding
    if (wheelFlow === 'gex-first' && moduleDef.id === 'explore-gex') {
      startWheelOnboarding('gex-first');
      return;
    }

    setShowNextModulePrompt(moduleDef);
    fetch('/api/user/achievements', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ achievement: moduleDef.achievementKey, completed: true }),
    }).then(() => {
      if (typeof window !== 'undefined') window.dispatchEvent(new Event('achievements-updated'));
    }).catch(() => {});
  };

  const skipModule = () => {
    const moduleDef = effectiveTourModules.find((m) => m.id === activeModuleId);
    if (moduleListenerCleanupRef.current) {
      moduleListenerCleanupRef.current();
      moduleListenerCleanupRef.current = null;
    }
    cleanupHighlights();
    if (typeof window !== 'undefined') (window as unknown as { __tourSidebarLocked?: boolean }).__tourSidebarLocked = false;
    setActiveModuleId(null);
    setModuleSubStepIndex(0);
    setModuleCompletedSubSteps([]);
    setCurrentStepInfo(null);
    setIsActive(false);
    if (moduleDef) setShowNextModulePrompt(moduleDef);
  };

  const advanceModule = () => {
    const moduleDef = effectiveTourModules.find((m) => m.id === activeModuleId);
    if (!moduleDef) return;
    const subSteps = moduleDef.subSteps;
    const current = subSteps[moduleSubStepIndex];
    if (!current) return;
    setModuleCompletedSubSteps((prev) => [...prev, current.id]);
    if (moduleSubStepIndex + 1 < subSteps.length) {
      setModuleSubStepIndex((prev) => prev + 1);
    } else {
      completeModule(moduleDef);
    }
  };
  const advanceModuleRef = React.useRef(advanceModule);
  advanceModuleRef.current = advanceModule;

  const goBackModule = () => {
    if (moduleSubStepIndex <= 0) return;
    setModuleSubStepIndex((prev) => prev - 1);
    setModuleCompletedSubSteps((prev) => prev.slice(0, -1));
  };

  const startModule = (moduleId: string) => {
    const moduleDef = effectiveTourModules.find((m) => m.id === moduleId);
    if (!moduleDef) return;
    moduleStartingRef.current = true;
    setShowNextModulePrompt(null);
    // For save-search, force Advanced mode for filters & save
    if (moduleId === 'save-search' && typeof window !== 'undefined') {
      try { window.localStorage.setItem('income-view-mode', '"advanced"'); } catch {}
      window.dispatchEvent(new CustomEvent('income-view-mode-change', { detail: { mode: 'advanced' } }));
    }
    const targetPath = moduleDef.targetPath;
    const onPath = pathname === targetPath || (targetPath !== '/dashboard' && pathname.startsWith(targetPath));
    const run = () => {
      if (typeof window !== 'undefined') (window as unknown as { __tourSidebarLocked?: boolean }).__tourSidebarLocked = true;
      // On desktop, expand sidebar so navigation links are visible; on mobile, skip -
      // the sidebar sheet will be opened on-demand by steps that target sidebar elements.
      const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
      if (!isMobile) ensureSidebarIsExpanded();
      setActiveModuleId(moduleId);
      setModuleSubStepIndex(0);
      setModuleCompletedSubSteps([]);
      setIsActive(true);
      moduleStartingRef.current = false;
    };
    if (!onPath) {
      router.push(targetPath);
      setTimeout(run, 800);
    } else {
      const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
      if (isMobile) {
        // On mobile, just run immediately with a short delay - no sidebar opening
        setTimeout(run, 300);
      } else {
        const sidebarResult = ensureSidebarIsExpanded();
        if (sidebarResult instanceof Promise) {
          sidebarResult.then(() => setTimeout(run, 300));
        } else {
          setTimeout(run, 300);
        }
      }
    }
  };

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ moduleId: string; subStepId: string }>).detail;
      if (!detail || detail.moduleId !== activeModuleId) return;
      const moduleDef = effectiveTourModules.find((m) => m.id === activeModuleId);
      if (!moduleDef) return;
      const current = moduleDef.subSteps[moduleSubStepIndex];
      // Exact match: advance one step
      if (detail.subStepId === current?.id) {
        advanceModuleRef.current();
        return;
      }
      // GEX: Generate clicked while on earlier steps -> skip ahead to review
      if (activeModuleId === 'explore-gex' && detail.subStepId === 'g3-generate' && moduleSubStepIndex < 3) {
        setModuleCompletedSubSteps((prev) => Array.from(new Set([...prev, 'g1-ticker', 'g2-expirations', 'g3-generate'])));
        setModuleSubStepIndex(3);
        return;
      }
    };
    window.addEventListener(TOUR_SUBSTEP_COMPLETE_EVENT, handler);
    return () => window.removeEventListener(TOUR_SUBSTEP_COMPLETE_EVENT, handler);
  }, [activeModuleId, moduleSubStepIndex]);

  useEffect(() => {
    if (!activeModuleId) return;
    const moduleDef = effectiveTourModules.find((m) => m.id === activeModuleId);
    if (!moduleDef) return;
    const subSteps = moduleDef.subSteps;
    const subStep = subSteps[moduleSubStepIndex];
    if (!subStep) return;

    const applyStep = () => {
      setCurrentStepInfo({
        id: subStep.id,
        title: subStep.title,
        text: subStep.text,
        progressLabel: `${moduleDef.title} - Step ${moduleSubStepIndex + 1} of ${subSteps.length}`,
        isFirst: moduleSubStepIndex === 0,
        isLast: moduleSubStepIndex === subSteps.length - 1,
        moduleTitle: moduleDef.title,
        subStepIndex: moduleSubStepIndex,
        totalSubSteps: subSteps.length,
        completedSubSteps: moduleCompletedSubSteps,
        subStepTitles: subSteps.map((s) => s.title),
      });
      highlightForSubStep(subStep);
      attachCompletionListener(subStep, () => advanceModuleRef.current());
    };

    const primary = `[data-shepherd-highlight-target="${subStep.highlightTarget}"]`;
    const fallback = subStep.highlightFallbackTarget
      ? `[data-shepherd-highlight-target="${subStep.highlightFallbackTarget}"]`
      : null;
    const hasTarget = () => {
      if (queryVisible(primary)) return true;
      if (fallback && queryVisible(fallback)) return true;
      return false;
    };

    // On mobile, if this step targets a sidebar element (ends with "-page"),
    // open the sidebar sheet first so the element becomes visible.
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
    const targetsSidebar = subStep.highlightTarget.endsWith('-page');
    if (isMobile && targetsSidebar) {
      ensureMobileSidebarIsOpen();
    }

    const fallbackTimeout = setTimeout(applyStep, 5000);
    let rafId: number;
    const poll = () => {
      if (hasTarget()) {
        clearTimeout(fallbackTimeout);
        applyStep();
        return;
      }
      rafId = requestAnimationFrame(poll);
    };
    rafId = requestAnimationFrame(poll);

    return () => {
      clearTimeout(fallbackTimeout);
      cancelAnimationFrame(rafId);
    };
  }, [activeModuleId, moduleSubStepIndex, moduleCompletedSubSteps]);

  // Modify this function to prevent tour elements from being clicked
  const handleDocumentClick = (event: MouseEvent) => {
    // If the tour is active and mobile sidebar was opened by the tour
    if (isActive && (window as any).__tourMobileButtonClicked) {
      // Check if the click is on a close button or outside the mobile sheet
      const target = event.target as Element;
      const closeButton = target.closest('button[aria-label="Close"]');
      
      // If clicking close button or outside the sheet, prevent it
      if (closeButton || target.getAttribute('role') === 'dialog') {
        // console.log('Preventing sidebar from closing during tour');
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    }
  };

  // Clean up document click handler when tour is active
  useEffect(() => {
    if (isActive) {
      document.addEventListener('click', handleDocumentClick, true);
      return () => {
        document.removeEventListener('click', handleDocumentClick, true);
        (window as any).__tourMobileButtonClicked = false;
        // Clear sidebar expansion flag on cleanup
        window.__sidebarExpansionInProgress = false;
      };
    }
  }, [isActive]);


  // API for updating user's onboarding status
  const updateOnboardingStatus = async (completed: boolean) => {
    try {
      const response = await fetch('/api/user/onboarding', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ completed }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update onboarding status');
      }
      
      // Update local state
      setHasCompletedOnboarding(completed);
    } catch (error) {
      console.error('Error updating onboarding status:', error);
    }
  };

  const startTourWithInterests = (interests: string[]) => {
    const hasDirectional = interests.includes('directional_trader');
    const hasPremium = interests.includes('premium_collector');

    // Legacy values - treat old "gex" like directional_trader, everything else like premium_collector
    const legacyGex = !hasDirectional && !hasPremium && interests.includes('gex');

    if ((hasDirectional && !hasPremium) || legacyGex) {
      startGexOnboarding();
      trackClientEvent({ name: 'onboarding-flow-selected', params: { flow: 'gex', interests } });
    } else {
      // premium_collector only, both selected, or any legacy wheel-type interest → wheel tour first
      startWheelOnboarding('wheel');
      trackClientEvent({ name: 'onboarding-flow-selected', params: { flow: 'wheel', interests } });
    }
  };

  const startTour = () => {
    startTourWithInterests(onboardingInterests);
  };

  const endTour = () => {
    if (activeModuleId) skipModule();
  };

  const startTourAtStep = (stepId: string) => {
    const moduleId = STEP_ID_TO_MODULE_ID[stepId] ?? 'explore-screener';
    startModule(moduleId);
  };

  // While the dashboard-level TermsAcceptanceGate is up we must not render
  // any tour overlay. Otherwise tour modals (z-[10002]) sit visually on top
  // of the terms gate (Radix AlertDialog at z-50), the user can't interact
  // with them (the gate's modal layer eats pointer events), and the only
  // way out is a hard refresh. State updates still proceed normally so the
  // tour resumes immediately after the user accepts the terms.
  const showTourUI = termsAccepted;

  return (
    <OnboardingContext.Provider value={{ hasCompletedOnboarding, setHasCompletedOnboarding, onboardingInterests }}>
      <TourContext.Provider value={{ startTour, startTourAtStep, startModule, endTour, isActive, activeModuleId, updateOnboardingStatus, startWheelOnboarding, startGexOnboarding }}>
        <OnboardingModeContext.Provider value={onboardingModeValue}>
        {showTourUI && showInterestsModal && (
          <OnboardingInterestsModal
            open={true}
            onboardingInterests={onboardingInterests}
            sandbox={!session?.user?.id}
            onInterestsSaved={(interests) => {
              setOnboardingInterests(interests);
              if (!session?.user?.id) {
                setSandboxInterests(interests);
              }
            }}
            onComplete={(interests) => {
              setShowPreferencesStep(false);
              startTourWithInterests(interests);
            }}
          />
        )}
        {showTourUI && isActive && currentStepInfo && (
          <TourStepModal
            open={true}
            step={currentStepInfo}
            onBack={() => {
              if (activeModuleId) goBackModule();
            }}
            onNext={() => {
              if (activeModuleId) advanceModule();
            }}
            onSkip={() => {
              if (activeModuleId) skipModule();
            }}
          />
        )}
        {showTourUI && wheelOnboardingActive && <WheelOnboardingProgress />}
        {showTourUI && gexOnboardingActive && <GexOnboardingProgress />}
        {showTourUI && showBrokerConnect && <BrokerConnectStep />}
        {showTourUI && showOnboardingPricing && <OnboardingPricingModal />}
        {showTourUI && showTourGuard && <WheelTourExitGuard />}
        {/* Settings stays reachable for expired users so they can manage
            billing or delete their account themselves. */}
        {showTourUI && showExpiredGate && !pathname?.startsWith('/dashboard/settings') && (
          <ExpiredSubscriptionGate open={showExpiredGate} onOpenChange={setShowExpiredGate} />
        )}
        {showTourUI && showNextModulePrompt && (
          <div className="fixed z-[10002] w-full max-w-md sm:bottom-6 sm:right-6 sm:left-auto left-0 right-0 bottom-0 sm:bottom-4 sm:rounded-lg rounded-t-lg overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-200">
            <div className="w-full rounded-t-lg sm:rounded-lg border border-border/50 bg-background shadow-lg overflow-hidden">
              <div className="h-1 bg-gradient-to-r from-transparent via-zinc-300 dark:via-zinc-600 to-transparent" />
              <div className="p-4 space-y-3">
                <p className="font-semibold text-sm">Module complete!</p>
                <p className="text-xs text-muted-foreground">Continue with the next module or explore on your own.</p>
                <div className="flex flex-col sm:flex-row gap-2">
                  {(() => {
                    const idx = effectiveTourModules.findIndex((m) => m.id === showNextModulePrompt.id);
                    const nextModule = effectiveTourModules[(idx + 1) % effectiveTourModules.length];
                    return (
                      <>
                        <button
                          type="button"
                          className="inline-flex items-center justify-center rounded-md text-sm font-medium bg-foreground text-background hover:bg-foreground/90 h-9 px-4 flex-1 sm:flex-initial"
                          onClick={() => {
                            setShowNextModulePrompt(null);
                            startModule(nextModule.id);
                          }}
                        >
                          Next: {nextModule.title}
                        </button>
                        <button
                          type="button"
                          className="inline-flex items-center justify-center rounded-md text-sm font-medium border border-border bg-background hover:bg-muted/50 h-9 px-4"
                          onClick={() => {
                            setShowNextModulePrompt(null);
                            updateOnboardingStatus(true);
                          }}
                        >
                          Done
                        </button>
                      </>
                    );
                  })()}
                </div>
                <div className="text-xs space-y-1.5 pt-1">
                  <p className="text-muted-foreground font-medium">All modules:</p>
                  <ul className="space-y-1 pl-0">
                    {effectiveTourModules.map((m) => {
                      const isDone = completedModuleIds.includes(m.id);
                      return (
                        <li key={m.id} className="flex items-center gap-2">
                          {isDone ? (
                            <span className="text-green-500 text-xs">&#10003;</span>
                          ) : (
                            <span className="text-muted-foreground/40 text-xs">&#9675;</span>
                          )}
                          <button
                            type="button"
                            className={`text-left ${isDone ? 'text-muted-foreground' : 'text-primary hover:underline'}`}
                            onClick={() => {
                              setShowNextModulePrompt(null);
                              startModule(m.id);
                            }}
                          >
                            {m.title}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
        {children}
        </OnboardingModeContext.Provider>
      </TourContext.Provider>
    </OnboardingContext.Provider>
  );
};
